import { supabase } from '@/integrations/supabase/client';
import { useEffect, useState } from 'react';

export interface CurrencyRate {
  currency_code: string;
  rate_to_usd: number;
  symbol: string;
}

// Cache object to store the currency rates across component instances
let cachedRates: { [key: string]: CurrencyRate } | null = null;
let cacheTime: number | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

export const useCurrencyRates = () => {
  const [currencyRates, setCurrencyRates] = useState<{
    [key: string]: CurrencyRate;
  } | null>(cachedRates);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCurrencyRates = async () => {
      // Check if we have valid cached data
      if (cachedRates && cacheTime && Date.now() - cacheTime < CACHE_DURATION) {
        setCurrencyRates(cachedRates);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const { data: ratesData, error: ratesError } = await supabase
          .from('currency_rates')
          .select('*');

        if (ratesError) throw ratesError;

        const ratesMap = ratesData.reduce((acc, rate) => {
          acc[rate.currency_code] = rate;
          return acc;
        }, {} as { [key: string]: CurrencyRate });

        // Update cache
        cachedRates = ratesMap;
        cacheTime = Date.now();
        
        setCurrencyRates(ratesMap);
      } catch (err) {
        console.error('Error loading currency rates:', err);
        setError(err instanceof Error ? err.message : 'Failed to load currency rates');
      } finally {
        setIsLoading(false);
      }
    };

    loadCurrencyRates();
  }, []);

  return { currencyRates, isLoading, error };
};